/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState } from 'react';
import {
  Crop,
  PixelCrop,
  ReactCrop,
  centerCrop,
  makeAspectCrop
} from 'react-image-crop';
import { useDebounceEffect } from '../../../hooks/useDebounceEffect';
import 'react-image-crop/dist/ReactCrop.css';
import { canvasPreview } from '../../../utils/logo/canvasPreview';
import InfoButton from '../../chat/Button/InfoButton';
import { StyledCropButtonWrapper, StyledImageCropWrapper } from './Styled';
import RotateLeft from '../../icons/RotateLeft';
const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) =>
  centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );

const LogoUpload = ({
  onChange,
  onCancel,
  imgSrc,
  displayLarge
}: {
  onChange: (blob: Blob) => void;
  onCancel: () => void;
  imgSrc: string;
  displayLarge?: boolean;
}) => {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(1);

  useEffect(() => {
    if (!imgSrc) {
      setCrop(undefined);
    }
  }, [imgSrc]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  };

  const uploadBlob = () => {
    if (!previewCanvasRef.current) {
      throw new Error('Canvasは存在しません。');
    }

    previewCanvasRef.current.toBlob(
      (blob) => {
        if (!blob) {
          throw new Error('BLOB作成失敗。');
        }
        onChange(blob);
      },
      'image/jpeg',
      0.8
    );
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
        uploadBlob();
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  return (
    <>
      {!!imgSrc && (
        <StyledImageCropWrapper displayLarge={displayLarge}>
          <ReactCrop
            crop={crop}
            circularCrop
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              style={{
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                maxHeight: 300,
                maxWidth: displayLarge ? '100%' : 300
              }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
          <StyledCropButtonWrapper>
            <InfoButton
              label="キャンセル"
              size="sm"
              icon={
                <RotateLeft
                  width={displayLarge ? '16px' : '12px'}
                  height={displayLarge ? '15.4px' : '11.587px'}
                />
              }
              iconSize="auto"
              onClick={onCancel}
            />
          </StyledCropButtonWrapper>
        </StyledImageCropWrapper>
      )}
      {!!completedCrop && (
        <canvas
          ref={previewCanvasRef}
          style={{
            border: '1px solid black',
            objectFit: 'contain',
            width: completedCrop.width,
            height: completedCrop.height,
            display: 'none'
          }}
        />
      )}
    </>
  );
};

export default LogoUpload;
